import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetTimecardByIdQuery, useUpdateTimecardMutation } from 'store/api/timecards';
import { TimecardDetails, TimecardDto } from 'types/timecards';
import { captureTimecardUpdated } from 'utils/timecardAnalytics';
import {
    TIMECARD_SECTION_FIELDS,
    TimecardFormContext,
    timecardDtoToForm,
    timecardUpdateRequest,
} from '../timecardFormMapping';
import { useTimecardFormContext } from '../useTimecardFormContext';
import { AuthorFields } from './AuthorFields';
import { CaseFields } from './CaseFields';
import { DescriptionFields } from './DescriptionFields';
import { ExpensesFields } from './ExpensesFields';
import { PracticeAreaFields } from './PracticeAreaFields';
import { TimeBillingFields } from './TimeBillingFields';
import { useTimecardForm } from './useTimecardForm';

/**
 * The title of a group of fields inside the Timecard card, the same over its read rows and over its inputs: a block
 * across the card's grid in both.
 */
export function TimecardGroupTitle({ id }: { id: string }) {
    return (
        <h4 className="text-label text-muted-foreground border-neutral-tint col-span-full m-0 border-t pt-3.5 uppercase">
            <FormattedMessage id={id} />
        </h4>
    );
}

interface SectionFormProps {
    timecard: TimecardDetails;
    initialData: TimecardDto;
    context: TimecardFormContext;
    onClose: () => void;
}

function TimecardSectionForm({ timecard, initialData, context, onClose }: SectionFormProps) {
    const form = useTimecardForm({ type: 'edit', initialData, timecard, inPlace: true });
    // The page's own cache entry, so the refetch after the save refreshes it.
    const { refetch } = useGetTimecardByIdQuery(timecard.id);
    const [updateTimecard] = useUpdateTimecardMutation();

    const onSave = (values: TimecardDto) =>
        saveSection(values, {
            section: 'timecard',
            sectionFields: TIMECARD_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => timecardDtoToForm(dto, context),
            toRequest: (edited, _latest, latestDto) => timecardUpdateRequest(edited, latestDto),
            update: (request) => updateTimecard(request).unwrap(),
            onSaved: () => {
                void captureTimecardUpdated({
                    via: 'record',
                    descriptionChanged: values.description !== timecard.description,
                    description: values.description,
                    timecardLanguageCode: form.currentMatter?.timecardLanguageCode,
                });
                onClose();
            },
        });

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose} formKey="timecard-edit">
            <CaseFields form={form} />
            <AuthorFields form={form} />
            <PracticeAreaFields form={form} />
            <TimecardGroupTitle id="timecards.form.description" />
            <DescriptionFields form={form} />
            <TimecardGroupTitle id="timecards.time-and-billing" />
            <TimeBillingFields form={form} />
            <TimecardGroupTitle id="expenses.expenses" />
            <ExpensesFields form={form} />
        </SectionEditForm>
    );
}

function TimecardSectionEditor({ timecard, onClose }: { timecard: TimecardDetails; onClose: () => void }) {
    const { context, isLoading } = useTimecardFormContext();
    const initialData = useFrozenOnceLoaded(!isLoading, () => timecardDtoToForm(timecard, context));
    if (!initialData) {
        return <Loader />;
    }
    return <TimecardSectionForm timecard={timecard} initialData={initialData} context={context} onClose={onClose} />;
}

interface TimecardSectionProps {
    timecard: TimecardDetails;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/**
 * The record's one editable card: every field of the timecard under one Edit and one Save, as its form page
 * edits it. Its id is the `?edit=` value the timecard's edit URL redirects with.
 */
export function TimecardSection({ timecard, children, rows }: TimecardSectionProps) {
    return (
        <SectionEdit
            id="timecard"
            titleId="timecards.timecard"
            editor={(close) => <TimecardSectionEditor timecard={timecard} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
